import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import FocusLock from 'react-focus-lock'
import { Search } from 'lucide-react'
import type { GenderId } from '@/lib/baba-api'
import { SPEAKING_TO_OPTIONS } from './constants'
import { WsKbd } from './workspace'

interface CommandItem {
  id: string
  label: string
  kbd?: string
  run: () => void
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  onNewTranslation: () => void
  onTranslate: () => void
  onSpeak: () => void
  onCopyTranslation: () => void
  onCopyTransliteration: () => void
  onSwap: () => void
  onSave: () => void
  onSetSpeakingTo: (v: GenderId) => void
}

export function CommandPalette(props: CommandPaletteProps) {
  const { open, onClose } = props
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  const items = useMemo<CommandItem[]>(() => [
    { id: 'new', label: 'New Translation', kbd: '⌘N', run: props.onNewTranslation },
    { id: 'translate', label: 'Translate', kbd: '⌘↵', run: props.onTranslate },
    { id: 'listen', label: 'Listen to Translation', kbd: '⌘L', run: props.onSpeak },
    { id: 'copy', label: 'Copy Translation', kbd: '⇧⌘C', run: props.onCopyTranslation },
    { id: 'copy-translit', label: 'Copy Transliteration', run: props.onCopyTransliteration },
    { id: 'swap', label: 'Swap Languages', kbd: '⌘⇧S', run: props.onSwap },
    { id: 'save', label: 'Save to Library', kbd: '⌘S', run: props.onSave },
    ...SPEAKING_TO_OPTIONS.map((o) => ({
      id: `to-${o.id}`,
      label: `Speaking To: ${o.label}`,
      run: () => props.onSetSpeakingTo(o.id),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [
    props.onNewTranslation, props.onTranslate, props.onSpeak, props.onCopyTranslation,
    props.onCopyTransliteration, props.onSwap, props.onSave,
    props.onSetSpeakingTo,
  ])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((i) => i.label.toLowerCase().includes(q))
  }, [items, query])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setIndex(0)
    const t = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => { setIndex(0) }, [query])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => Math.min(i + 1, filtered.length - 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); return }
      if (e.key === 'Enter') {
        e.preventDefault()
        const item = filtered[index]
        if (item) { item.run(); onClose() }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, filtered, index, onClose])

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${index}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [index])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center pt-[18vh] animate-fade-in backdrop-blur-sm"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <FocusLock returnFocus autoFocus>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          className="w-[320px] flex flex-col overflow-hidden animate-scale-in rounded-md"
          style={{
            border: '1px solid var(--line)',
            background: 'var(--card)',
            boxShadow: 'var(--shadow-lg)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 h-9 px-2.5 border-b" style={{ borderColor: 'var(--line-hair)', color: 'var(--muted-2)' }}>
            <Search size={14} aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type a command…"
              aria-label="Search commands"
              className="flex-1 min-w-0 bg-transparent text-[13px] focus:outline-none"
              style={{ color: 'var(--ink)' }}
            />
          </div>
          <div className="font-mono px-2.5 pt-2 pb-1 uppercase font-semibold" style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--muted-2)' }}>
            Actions
          </div>
          <div ref={listRef} className="relative max-h-[320px] overflow-y-auto px-1 pb-2 [scrollbar-width:thin]">
            {filtered.length === 0 ? (
              <p className="text-[12.5px] text-center py-4" style={{ color: 'var(--muted-2)' }}>No matching commands</p>
            ) : (
              filtered.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  data-idx={i}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => { item.run(); onClose() }}
                  className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left transition-colors duration-100 rounded cursor-pointer select-none"
                  style={{
                    background: i === index ? 'var(--accent-soft)' : 'transparent',
                  }}
                >
                  <span className="text-[13px] font-medium truncate" style={{ color: i === index ? 'var(--accent-ink)' : 'var(--ink)' }}>{item.label}</span>
                  {item.kbd && <WsKbd>{item.kbd}</WsKbd>}
                </button>
              ))
            )}
          </div>
        </div>
      </FocusLock>
    </div>,
    document.body,
  )
}
